import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, Search, Trash2, RotateCcw, Factory, DollarSign, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Receipt from "@/components/Receipt";
import { useAuth } from "@/AuthContext";
import { cleanItemName } from "@/api";
import { Money, HideMoneyButton, useHiddenValues } from "@/contexts/HiddenValues";

// O olho de "ocultar valores" agora usa o sistema GLOBAL (HiddenValues),
// mascarando faturamento, total e lucro em conjunto (não só lucro).

function summarizeItems(items, productsMap = {}) {
  if (!items || items.length === 0) return "—";
  const parts = items.map(it => {
    const name = it.name || "Item";
    const qty = Number(it.quantity || 0);
    const prod = it.product_id ? productsMap[it.product_id] : null;
    const code = prod && prod.code ? ` [${prod.code}]` : "";
    return qty > 1 ? `${qty}× ${name}${code}` : `${name}${code}`;
  });
  return parts.join(", ");
}

export default function Historico() {
  const { canSeeFinance, user } = useAuth();
  // ADM/GERENTE/MASTER podem ver e filtrar por vendedor.
  const canFilterSeller = user && ["master", "owner", "gerente"].includes(user.role);
  const [tab, setTab] = useState("ativas"); // 'ativas' | 'canceladas'
  const [orders, setOrders] = useState([]);
  const [cancelled, setCancelled] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [sellerFilter, setSellerFilter] = useState("all");
  const [store, setStore] = useState({});
  const [showOrder, setShowOrder] = useState(null);
  const [suppliersDialog, setSuppliersDialog] = useState(null);
  const [productsMap, setProductsMap] = useState({});
  const [suppliersMap, setSuppliersMap] = useState({});
  const [q, setQ] = useState("");
  const { hidden: moneyHidden } = useHiddenValues();

  // ── Filtro de data com persistência (localStorage) ────────────────────
  // Presets: "today" | "week" | "month" (mês atual) | "range" (De/Até) |
  //          "month_specific" (mês YYYY-MM navegável) | "all".
  const DATE_PRESET_KEY = "gestaoexpert:historico:date_preset";
  const DATE_SPECIFIC_KEY = "gestaoexpert:historico:date_specific";
  const DATE_FROM_KEY = "gestaoexpert:historico:date_from";
  const DATE_TO_KEY = "gestaoexpert:historico:date_to";
  const MONTH_SPECIFIC_KEY = "gestaoexpert:historico:month_specific";
  const [datePreset, setDatePreset] = useState(() => {
    try { return localStorage.getItem(DATE_PRESET_KEY) || "all"; } catch { return "all"; }
  });
  const [dateSpecific, setDateSpecific] = useState(() => {
    try { return localStorage.getItem(DATE_SPECIFIC_KEY) || new Date().toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  });
  const [dateFrom, setDateFrom] = useState(() => {
    try { return localStorage.getItem(DATE_FROM_KEY) || new Date().toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  });
  const [dateTo, setDateTo] = useState(() => {
    try { return localStorage.getItem(DATE_TO_KEY) || new Date().toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  });
  const [monthSpecific, setMonthSpecific] = useState(() => {
    try { return localStorage.getItem(MONTH_SPECIFIC_KEY) || new Date().toISOString().slice(0, 7); } catch { return new Date().toISOString().slice(0, 7); }
  });
  useEffect(() => {
    try { localStorage.setItem(DATE_PRESET_KEY, datePreset); } catch { /* localStorage indisponível */ }
  }, [datePreset]);
  useEffect(() => {
    try { localStorage.setItem(DATE_SPECIFIC_KEY, dateSpecific); } catch { /* localStorage indisponível */ }
  }, [dateSpecific]);
  useEffect(() => {
    try { localStorage.setItem(DATE_FROM_KEY, dateFrom); } catch { /* localStorage indisponível */ }
  }, [dateFrom]);
  useEffect(() => {
    try { localStorage.setItem(DATE_TO_KEY, dateTo); } catch { /* localStorage indisponível */ }
  }, [dateTo]);
  useEffect(() => {
    try { localStorage.setItem(MONTH_SPECIFIC_KEY, monthSpecific); } catch { /* localStorage indisponível */ }
  }, [monthSpecific]);

  // ── Modal de comissão (edita commission_amount e commission_status) ──
  const [commissionDialog, setCommissionDialog] = useState(null);

  const sellerById = useMemo(() => Object.fromEntries(sellers.map(s => [s.id, s])), [sellers]);
  const sellerLabel = (o) => {
    if (o.created_by_user_id && sellerById[o.created_by_user_id]) {
      return sellerById[o.created_by_user_id].label;
    }
    return o.created_by || "—";
  };

  // Data efetiva da venda no Histórico:
  //   1) finalized_at — quando o pedido foi finalizado (movido para histórico).
  //   2) paid_at      — fallback caso esteja marcado como Pago sem ter sido Finalizado.
  //   3) created_at   — fallback para vendas legadas sem esses timestamps.
  // Vendas criadas direto no PDV têm finalized_at = created_at, então o display
  // não muda para o fluxo comum. Vendas que vieram do Kanban (Pendente → Finalizado)
  // passam a mostrar a data em que o usuário moveu o pedido para histórico,
  // não a data em que o pedido foi originalmente criado.
  const saleDate = (o) => o.finalized_at || o.paid_at || o.created_at;

  const load = async () => {
    const calls = [
      api.get("/orders", { params: { history: true, ...(sellerFilter !== "all" ? { seller_id: sellerFilter } : {}) } }),
      api.get("/orders", { params: { cancelled: true, ...(sellerFilter !== "all" ? { seller_id: sellerFilter } : {}) } }),
      api.get("/store"),
    ];
    if (canSeeFinance) {
      calls.push(api.get("/products", { params: { light: true } }));
      calls.push(api.get("/suppliers"));
    }
    const [o, c, s, p, sup] = await Promise.all(calls);
    setOrders(o.data); setCancelled(c.data); setStore(s.data);
    if (canSeeFinance) {
      const pm = {}; (p.data || []).forEach(pr => { pm[pr.id] = pr; });
      const sm = {}; (sup.data || []).forEach(sp => { sm[sp.id] = sp; });
      setProductsMap(pm); setSuppliersMap(sm);
    }
  };
  useEffect(() => { load(); }, [canSeeFinance, sellerFilter]); // eslint-disable-line

  // Carrega lista de vendedores (apenas para roles autorizados)
  useEffect(() => {
    if (!canFilterSeller) return;
    api.get("/sellers").then(r => setSellers(r.data || [])).catch(() => {});
  }, [canFilterSeller]);

  const openSuppliers = (o) => {
    const rows = (o.items || []).map(it => {
      const product = it.product_id ? productsMap[it.product_id] : null;
      // Prioriza supplier_id do PRÓPRIO item (caso avulso, gravado pela tela
      // de Vendas com "Troca + obs"). Se não houver, cai para o supplier do
      // produto cadastrado.
      const supplierId = it.supplier_id || product?.supplier_id || null;
      const supplier = supplierId ? suppliersMap[supplierId] : null;
      // Observação do fornecedor (preenchida apenas em casos de "Troca").
      // Pode vir do PRÓPRIO item (vendas avulsas — fluxo Vendas → Produto avulso)
      // ou do CADASTRO do produto (Produtos → Fornecedor=Troca → Observação da Troca).
      const supplierNote = (it.supplier_note || product?.supplier_note || "").trim();
      // Data da compra ao fornecedor (produto avulso na venda OU cadastro do produto).
      const purchaseDate = it.supplier_purchase_date || product?.supplier_purchase_date || null;
      return {
        item_name: cleanItemName(it.name) || "Item",
        quantity: it.quantity || 1,
        product_code: product?.code || "",
        supplier_name: supplier?.name || null,
        supplier_contact: supplier?.contact || "",
        supplier_note: supplierNote,
        supplier_purchase_date: purchaseDate,
      };
    });
    setSuppliersDialog({ order_number: o.order_number, rows, date: saleDate(o) });
  };

  const remove = async (oid) => {
    if (!window.confirm("Excluir esta venda? O estoque será devolvido e o valor será removido do faturamento e lucro.")) return;
    try {
      await api.delete(`/orders/${oid}`);
      toast.success("Venda cancelada e estoque devolvido");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  const restore = async (oid) => {
    if (!window.confirm("Restaurar esta venda? Ela voltará ao histórico ativo e o estoque será descontado novamente.")) return;
    try {
      await api.post(`/orders/${oid}/restore`);
      toast.success("Venda restaurada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao restaurar");
    }
  };

  const baseList = tab === "ativas" ? orders : cancelled;

  // Helper: retorna início/fim (ISO) do preset selecionado.
  const dateRange = useMemo(() => {
    const now = new Date();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    if (datePreset === "all") return null;
    if (datePreset === "today") return { s: startOfDay(now), e: endOfDay(now) };
    if (datePreset === "week") {
      const monday = new Date(now);
      const day = (monday.getDay() + 6) % 7; // seg=0 ... dom=6
      monday.setDate(monday.getDate() - day);
      return { s: startOfDay(monday), e: endOfDay(now) };
    }
    if (datePreset === "month") {
      return { s: new Date(now.getFullYear(), now.getMonth(), 1), e: endOfDay(now) };
    }
    if (datePreset === "specific" && dateSpecific) {
      // Legacy: mantido pra retrocompatibilidade caso alguém tenha "specific"
      // salvo no localStorage — comporta como "range" degenerado (1 dia).
      const d = new Date(`${dateSpecific}T00:00:00`);
      return { s: startOfDay(d), e: endOfDay(d) };
    }
    if (datePreset === "range" && dateFrom && dateTo) {
      let s = new Date(`${dateFrom}T00:00:00`);
      let e = new Date(`${dateTo}T00:00:00`);
      if (s > e) { const tmp = s; s = e; e = tmp; }
      return { s: startOfDay(s), e: endOfDay(e) };
    }
    if (datePreset === "month_specific" && monthSpecific) {
      const [yStr, mStr] = monthSpecific.split("-");
      const y = Number(yStr), m = Number(mStr) - 1;
      if (Number.isFinite(y) && Number.isFinite(m)) {
        const s = new Date(y, m, 1);
        const e = new Date(y, m + 1, 0, 23, 59, 59, 999); // último dia do mês
        return { s, e };
      }
    }
    return null;
  }, [datePreset, dateSpecific, dateFrom, dateTo, monthSpecific]);

  const filtered = useMemo(() => {
    let list = baseList;
    if (dateRange) {
      list = list.filter(o => {
        const dt = new Date(saleDate(o));
        return dt >= dateRange.s && dt <= dateRange.e;
      });
    }
    if (!q.trim()) return list;
    const term = q.trim().toLowerCase();
    return list.filter(o =>
      (o.customer_name || "").toLowerCase().includes(term) ||
      String(o.order_number).padStart(6,"0").includes(term) ||
      (o.items || []).some(it => (it.name || "").toLowerCase().includes(term))
    );
  }, [baseList, q, dateRange]); // eslint-disable-line

  const openReceipt = async (id) => {
    const r = await api.get(`/orders/${id}`);
    setShowOrder(r.data);
  };

  // ── Modal de Comissão ──────────────────────────────────────────────
  const openCommission = (o) => {
    setCommissionDialog({
      order_id: o.id,
      order_number: o.order_number,
      customer_name: o.customer_name,
      total: o.total,
      created_by: o.created_by,
      amount: Number(o.commission_amount || 0),
      status: o.commission_status || "pending",
    });
  };
  const saveCommission = async () => {
    if (!commissionDialog) return;
    const { order_id, amount, status } = commissionDialog;
    try {
      await api.put(`/orders/${order_id}/commission`, { amount: Number(amount) || 0, status });
      toast.success("Comissão salva");
      setCommissionDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar comissão");
    }
  };
  const removeCommission = async () => {
    if (!commissionDialog) return;
    if (!window.confirm("Remover a comissão desta venda?")) return;
    try {
      await api.delete(`/orders/${commissionDialog.order_id}/commission`);
      toast.success("Comissão removida");
      setCommissionDialog(null);
      load();
    } catch { toast.error("Erro ao remover"); }
  };

  const isCancelled = tab === "canceladas";

  // Totais agregados (respeitam filtros + busca + aba) — sempre visíveis
  // mesmo quando `hideProfit` está ON (lucro vira *****).
  // Subtrai comissões pagas dos pedidos filtrados (mesma regra do Financeiro).
  const totals = useMemo(() => {
    const list = filtered.filter(o => !o.cancelled_at && tab === "ativas");
    const revenue = list.reduce((s, o) => s + Number(o.total || 0), 0);
    const grossProfit = list.reduce((s, o) => s + Number(o.profit || 0), 0);
    const commissionsPaid = list.reduce(
      (s, o) => s + (o.commission_status === "paid" ? Number(o.commission_amount || 0) : 0),
      0,
    );
    return {
      count: list.length,
      revenue,
      profit: grossProfit - commissionsPaid,  // lucro já com desconto de comissões pagas
      grossProfit,
      commissionsPaid,
    };
  }, [filtered, tab]);

  return (
    <div className="space-y-6" data-testid="historico-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-tiny mb-2">Histórico</p>
          <h1 className="font-display text-4xl tracking-tight">Vendas finalizadas</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSeeFinance && (
            <HideMoneyButton testId="historico-hide-money"/>
          )}
          <button
            onClick={()=>setTab("ativas")}
            className={`text-xs px-3 py-1.5 rounded-md transition ${tab === "ativas" ? "bg-[#0A0A0A] text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`}
            data-testid="tab-ativas">
            Ativas <span className="ml-1 opacity-70">{orders.length}</span>
          </button>
          <button
            onClick={()=>setTab("canceladas")}
            className={`text-xs px-3 py-1.5 rounded-md transition border ${tab === "canceladas" ? "bg-red-100 text-red-800 border-red-300" : "bg-red-50/40 text-red-600 border-red-100 hover:bg-red-50"}`}
            data-testid="tab-canceladas">
            Canceladas / Excluídas <span className="ml-1 opacity-70">{cancelled.length}</span>
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por cliente, produto ou nº…" className="pl-9" data-testid="history-search-input"/>
        </div>
        {canFilterSeller && (
          <Select value={sellerFilter} onValueChange={setSellerFilter}>
            <SelectTrigger className="w-52" data-testid="history-seller-filter"><SelectValue placeholder="Vendedor"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os vendedores</SelectItem>
              {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {/* Filtro de data — presets + intervalo (De/Até) + mês específico navegável */}
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="w-44" data-testid="history-date-filter">
            <CalendarIcon size={13} className="mr-1.5 opacity-60"/>
            <SelectValue placeholder="Data"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as datas</SelectItem>
            <SelectItem value="today">Hoje</SelectItem>
            <SelectItem value="week">Esta semana</SelectItem>
            <SelectItem value="month">Este mês</SelectItem>
            <SelectItem value="range">Intervalo (De/Até)</SelectItem>
            <SelectItem value="month_specific">Mês específico</SelectItem>
          </SelectContent>
        </Select>
        {datePreset === "specific" && (
          <Input
            type="date"
            value={dateSpecific}
            onChange={(e)=>setDateSpecific(e.target.value)}
            className="w-40"
            data-testid="history-date-specific"
          />
        )}
        {datePreset === "range" && (
          <div className="flex items-center gap-1.5" data-testid="history-date-range">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e)=>setDateFrom(e.target.value)}
              className="w-40"
              data-testid="history-date-from"
              title="Data inicial"
            />
            <span className="text-xs text-neutral-500">até</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e)=>setDateTo(e.target.value)}
              className="w-40"
              data-testid="history-date-to"
              title="Data final"
            />
          </div>
        )}
        {datePreset === "month_specific" && (
          <div className="flex items-center gap-1" data-testid="history-month-specific">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => {
                const [y, m] = monthSpecific.split("-").map(Number);
                const d = new Date(y, m - 2, 1); // volta 1 mês
                setMonthSpecific(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
              }}
              data-testid="history-month-prev"
              title="Mês anterior"
            >
              <ChevronLeft size={14}/>
            </Button>
            <Input
              type="month"
              value={monthSpecific}
              onChange={(e)=>setMonthSpecific(e.target.value)}
              className="w-40"
              data-testid="history-month-input"
            />
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => {
                const [y, m] = monthSpecific.split("-").map(Number);
                const d = new Date(y, m, 1); // avança 1 mês
                setMonthSpecific(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
              }}
              data-testid="history-month-next"
              title="Próximo mês"
            >
              <ChevronRight size={14}/>
            </Button>
          </div>
        )}
      </div>

      {/* Totais agregados */}
      {tab === "ativas" && canSeeFinance && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="history-totals">
          <Card className="p-3 border-[#E5E5E5] shadow-none">
            <div className="text-xs text-neutral-500">Quantidade de vendas</div>
            <div className="font-display text-2xl mt-1" data-testid="total-count">{totals.count}</div>
          </Card>
          <Card className="p-3 border-[#E5E5E5] shadow-none">
            <div className="text-xs text-neutral-500">Faturamento total</div>
            <div className="font-display text-2xl mt-1" data-testid="total-revenue"><Money value={totals.revenue}/></div>
          </Card>
          <Card className="p-3 border-[#E5E5E5] shadow-none">
            <div className="text-xs text-neutral-500">Lucro total</div>
            <div className="font-display text-2xl mt-1 text-emerald-700" data-testid="total-profit"><Money value={totals.profit}/></div>
            {totals.commissionsPaid > 0 && (
              <div className="text-[10px] text-red-600 mt-1" data-testid="total-commissions-discount">
                (já descontado <Money value={totals.commissionsPaid}/> em comissões pagas)
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Mobile cards */}
      <div className="grid md:hidden gap-3">
        {filtered.map(o => (
          <Card key={o.id} className={`p-4 border-[#E5E5E5] shadow-none ${isCancelled ? "opacity-60" : ""}`} data-testid={`history-card-${o.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-neutral-500">#{String(o.order_number).padStart(6,'0')}</div>
                <div className={`font-medium mt-1 ${isCancelled ? "line-through text-neutral-500" : ""}`}>{o.customer_name || "Sem cliente"}</div>
                <div className="text-xs text-neutral-700 mt-0.5 line-clamp-2">{summarizeItems(o.items, productsMap)}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{new Date(saleDate(o)).toLocaleString('pt-BR')}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">Vendedor: <span className="font-medium text-neutral-700 dark:text-neutral-200">{sellerLabel(o)}</span></div>
                {o.lead_name && (
                  <div className="text-[10px] text-purple-700 dark:text-purple-300 mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950" data-testid={`history-lead-${o.id}`}>
                    Lead: {o.lead_name}
                  </div>
                )}
                {isCancelled && o.cancelled_at && <div className="text-[10px] text-red-600 mt-0.5">Cancelada em {new Date(o.cancelled_at).toLocaleString('pt-BR')}</div>}
                {(o.created_by || o.last_actor) && (
                  <div className="text-[9px] text-neutral-300 italic mt-0.5">
                    {o.created_by && `criado: ${o.created_by}`}
                    {o.created_by && o.last_actor && o.last_actor !== o.created_by && " · "}
                    {o.last_actor && o.last_actor !== o.created_by && `alterado: ${o.last_actor}`}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                {isCancelled ? (
                  <>
                    <div className="font-display text-lg line-through text-neutral-400"><Money value={o.total_original || 0}/></div>
                    <div className="text-[10px] text-red-600 uppercase">não conta no faturamento</div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-lg"><Money value={o.total}/></div>
                    <div className="text-xs text-green-700">+<Money value={o.profit}/></div>
                  </>
                )}
              </div>
            </div>
            <div className={`grid ${canSeeFinance ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"} gap-2 mt-3`}>
              <Button size="sm" variant="outline" onClick={()=>openReceipt(o.id)}><Eye size={12} className="mr-1"/> Recibo</Button>
              {canSeeFinance && (
                <Button size="sm" variant="outline" onClick={()=>openSuppliers(o)} className="text-[#1DA1FF] hover:text-[#3B82F6]" data-testid={`suppliers-${o.id}`} title="Ver fornecedores (interno)">
                  <Factory size={12} className="mr-1"/> Fornec.
                </Button>
              )}
              {canSeeFinance && !isCancelled && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={()=>openCommission(o)}
                  className={o.commission_status === "paid" ? "text-emerald-700 border-emerald-300" : (o.commission_amount > 0 ? "text-amber-700 border-amber-300" : "")}
                  data-testid={`commission-${o.id}`}
                  title="Gerenciar comissão do vendedor"
                >
                  <DollarSign size={12} className="mr-1"/>
                  {o.commission_amount > 0
                    ? (o.commission_status === "paid" ? `Pago ${brl(o.commission_amount)}` : `Pend. ${brl(o.commission_amount)}`)
                    : "Comissão"}
                </Button>
              )}
              {isCancelled
                ? <Button size="sm" variant="outline" className="text-emerald-700" onClick={()=>restore(o.id)}><RotateCcw size={12} className="mr-1"/> Restaurar</Button>
                : <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={()=>remove(o.id)} data-testid={`remove-${o.id}`}><Trash2 size={12} className="mr-1"/> Excluir</Button>}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center py-12 text-neutral-500 text-sm">Nenhuma venda.</div>}
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block border-[#E5E5E5] shadow-none">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-[#E5E5E5]">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Nº</th>
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Vendedor</th>
              <th className="px-4 py-3 font-medium">Produtos</th>
              <th className="px-4 py-3 font-medium">Data</th>
              <th className="px-4 py-3 font-medium text-right">Total</th>
              <th className="px-4 py-3 font-medium text-right">Lucro</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} className={`border-b border-[#E5E5E5] hover:bg-neutral-50 ${isCancelled ? "opacity-60 bg-red-50/20" : ""}`} data-testid={`history-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">#{String(o.order_number).padStart(6,'0')}</td>
                <td className={`px-4 py-3 ${isCancelled ? "line-through" : ""}`}>
                  {o.customer_name || "—"}
                  {o.lead_name && (
                    <div className="text-[10px] text-purple-700 dark:text-purple-300 mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950">
                      Lead: {o.lead_name}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`row-seller-${o.id}`}>{sellerLabel(o)}</td>
                <td className="px-4 py-3 text-xs text-neutral-700 max-w-[260px] truncate" title={summarizeItems(o.items, productsMap)}>{summarizeItems(o.items, productsMap)}</td>
                <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">
                  {new Date(saleDate(o)).toLocaleString('pt-BR')}
                  {isCancelled && o.cancelled_at && <div className="text-[10px] text-red-600">cancel. {new Date(o.cancelled_at).toLocaleString('pt-BR')}</div>}
                  {(o.created_by || o.last_actor) && (
                    <div className="text-[9px] text-neutral-300 italic">
                      {o.created_by && `criado: ${o.created_by}`}
                      {o.created_by && o.last_actor && o.last_actor !== o.created_by && " · "}
                      {o.last_actor && o.last_actor !== o.created_by && `alterado: ${o.last_actor}`}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {isCancelled
                    ? <span className="line-through text-neutral-400"><Money value={o.total_original || 0}/></span>
                    : <Money value={o.total}/>}
                </td>
                <td className="px-4 py-3 text-right">
                  {isCancelled
                    ? <span className="text-[11px] text-red-600">não conta</span>
                    : <span className="text-green-700"><Money value={o.profit}/></span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-1" onClick={()=>openReceipt(o.id)}>
                    <Eye size={12} className="mr-1"/> Recibo
                  </Button>
                  {canSeeFinance && (
                    <Button size="sm" variant="outline" className="mr-1 text-[#1DA1FF] hover:text-[#3B82F6]" onClick={()=>openSuppliers(o)} data-testid={`suppliers-${o.id}`} title="Fornecedores (interno)">
                      <Factory size={12} className="mr-1"/> Fornec.
                    </Button>
                  )}
                  {canSeeFinance && !isCancelled && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={"mr-1 " + (o.commission_status === "paid" ? "text-emerald-700 border-emerald-300" : (o.commission_amount > 0 ? "text-amber-700 border-amber-300" : ""))}
                      onClick={()=>openCommission(o)}
                      data-testid={`commission-${o.id}`}
                      title="Gerenciar comissão do vendedor"
                    >
                      <DollarSign size={12} className="mr-1"/>
                      {o.commission_amount > 0
                        ? (o.commission_status === "paid" ? `Pago ${brl(o.commission_amount)}` : `Pend. ${brl(o.commission_amount)}`)
                        : "Comissão"}
                    </Button>
                  )}
                  {isCancelled
                    ? <Button size="sm" variant="outline" className="text-emerald-700" onClick={()=>restore(o.id)} data-testid={`restore-${o.id}`}><RotateCcw size={12} className="mr-1"/> Restaurar</Button>
                    : <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={()=>remove(o.id)} data-testid={`remove-${o.id}`}><Trash2 size={12} className="mr-1"/> Excluir</Button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-neutral-500 text-sm">Nenhuma venda.</td></tr>}
          </tbody>
        </table>
      </Card>

      {showOrder && <Receipt sale={showOrder} store={store} wideLayout onClose={()=>setShowOrder(null)}/>}

      {/* Dialog de Fornecedores (interno — só visível ao admin/gerente) */}
      <Dialog open={!!suppliersDialog} onOpenChange={(o)=>{ if (!o) setSuppliersDialog(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="suppliers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory size={18} className="text-[#1DA1FF]"/> Fornecedores do pedido
            </DialogTitle>
          </DialogHeader>
          {suppliersDialog && (
            <div className="space-y-3">
              <div className="text-xs text-neutral-500">
                Venda #{String(suppliersDialog.order_number).padStart(6,'0')} · {new Date(suppliersDialog.date).toLocaleString('pt-BR')}
              </div>
              <div className="text-[11px] bg-amber-50 border border-amber-100 rounded-md px-3 py-2 text-amber-800">
                Informação <strong>interna</strong> — útil para garantias futuras. O cliente não visualiza esses dados.
              </div>
              <div className="divide-y divide-[#E5E5E5] border border-[#E5E5E5] rounded-md">
                {suppliersDialog.rows.map((r, i) => (
                  <div key={i} className="p-3" data-testid={`supplier-row-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.quantity}× {r.item_name}</div>
                        {r.product_code && <div className="text-[10px] text-neutral-400 font-mono">Cód.: {r.product_code}</div>}
                      </div>
                    </div>
                    <div className="mt-2 text-xs">
                      {r.supplier_name ? (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Factory size={11} className="text-[#1DA1FF]"/>
                            <span className="font-medium">{r.supplier_name}</span>
                          </div>
                          {r.supplier_contact && (
                            <div className="text-neutral-500 mt-0.5 ml-4">{r.supplier_contact}</div>
                          )}
                          {r.supplier_purchase_date && (
                            <div className="text-neutral-600 dark:text-neutral-300 mt-0.5 ml-4 text-[11px]" data-testid={`supplier-purchase-date-${i}`}>
                              Comprado em: <strong>{new Date(r.supplier_purchase_date + "T00:00:00").toLocaleDateString('pt-BR')}</strong>
                            </div>
                          )}
                          {r.supplier_note && (
                            <div className="mt-1 ml-4 px-2 py-1 rounded bg-amber-50 border border-amber-100 text-amber-900 text-[11px] whitespace-pre-wrap" data-testid={`supplier-note-${i}`}>
                              <span className="font-semibold">Obs.:</span> {r.supplier_note}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-neutral-400 italic">Sem fornecedor cadastrado para este produto.</div>
                      )}
                    </div>
                  </div>
                ))}
                {suppliersDialog.rows.length === 0 && (
                  <div className="p-4 text-center text-xs text-neutral-500">Sem itens.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dialog de Comissão ─────────────────────────────────────────── */}
      <Dialog open={!!commissionDialog} onOpenChange={(o)=>{ if(!o) setCommissionDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="commission-dialog">
          <DialogHeader>
            <DialogTitle>Comissão do vendedor</DialogTitle>
          </DialogHeader>
          {commissionDialog && (
            <div className="space-y-3">
              <div className="text-xs text-neutral-500 bg-neutral-50 dark:bg-white/5 rounded-md p-3 border border-[#E5E5E5] dark:border-white/10">
                <div>Venda: <strong>#{String(commissionDialog.order_number || "").padStart(6, "0")}</strong></div>
                {commissionDialog.customer_name && <div>Cliente: <strong>{commissionDialog.customer_name}</strong></div>}
                <div>Vendedor: <strong>{commissionDialog.created_by || "—"}</strong></div>
                <div>Valor da venda: <strong>{brl(commissionDialog.total)}</strong></div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Valor da comissão</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={commissionDialog.amount || ""}
                  onChange={(e)=>setCommissionDialog(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0,00"
                  data-testid="commission-amount-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Status</label>
                <Select value={commissionDialog.status} onValueChange={(v)=>setCommissionDialog(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="commission-status-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
                {commissionDialog.status === "paid" && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    Ao salvar como <strong>Pago</strong>, o valor será lançado como despesa em todos os relatórios financeiros.
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-between items-center gap-2 mt-2">
            <Button variant="ghost" size="sm" className="text-red-600" onClick={removeCommission} disabled={!commissionDialog?.amount} data-testid="commission-remove-btn">
              Remover comissão
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setCommissionDialog(null)}>Cancelar</Button>
              <Button className="bg-[#0A0A0A] hover:bg-black text-white" onClick={saveCommission} data-testid="commission-save-btn">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
